import { apiFetch, apiFetchBlob, apiFetchRaw } from './client.js';

export type ReportType =
  | 'TRANSACTION_MONITORING_SUMMARY'
  | 'KYC_COMPLETION_REPORT'
  | 'SAR_SUBMISSION_LOG'
  | 'SANCTIONS_SCREENING_HIT_RATE'
  | 'DATA_RETENTION_COMPLIANCE'
  | 'CUSTOMER_COUNT_BY_JURISDICTION'
  | 'COMPLAINTS_RESOLUTION_SLA';

/** Every report is an aggregate or a short log, delivered as CSV (DEV-83). */
export type ReportFormat = 'CSV';
export type ReportJobStatus = 'COMPLETED' | 'FAILED';

export interface ReportCatalogEntry {
  type: ReportType;
  name: string;
  description: string;
  cadence: 'MONTHLY' | 'QUARTERLY' | 'ANNUAL';
  formats: ReportFormat[];
  /** Whether a jurisdiction may narrow it. Optional; every report runs without one. */
  jurisdictionFilter: boolean;
  /** False when there is no data to build it from yet. */
  available: boolean;
  unavailableReason?: string;
}

export interface ReportJob {
  id: string;
  reportType: ReportType;
  status: ReportJobStatus;
  format: ReportFormat;
  startDate: string;
  endDate: string;
  jurisdiction: string | null;
  requestedBy: string;
  /** Who asked, in words. */
  requestedByName: string | null;
  requestedAt: string;
  completedAt: string | null;
  /** Why it failed, in words an officer can act on. */
  errorMessage: string | null;
  rowCount: number | null;
  fileSize: number | null;
  downloadCount: number;
  correlationId: string;
}

export interface GenerateReportBody {
  startDate: string;
  endDate: string;
  format: ReportFormat;
  jurisdiction?: string;
}

export const reportsApi = {
  /** The catalogue, and the jurisdictions applications actually carry. */
  listCatalog: async (): Promise<{ reports: ReportCatalogEntry[]; jurisdictions: string[] }> => {
    const envelope = await apiFetchRaw<ReportCatalogEntry[]>('/admin/reports');
    if (envelope.error !== undefined || envelope.data === null) {
      throw new Error(envelope.error?.detail ?? 'The report catalogue could not be loaded.');
    }
    const meta = envelope.meta as { jurisdictions?: string[] } | undefined;
    return { reports: envelope.data, jurisdictions: meta?.jurisdictions ?? [] };
  },

  generate: (
    reportType: ReportType,
    body: GenerateReportBody,
  ): Promise<{ jobId: string; status: ReportJobStatus }> =>
    apiFetch<{ jobId: string; status: ReportJobStatus }>(
      `/admin/reports/${reportType}/generate`,
      { method: 'POST', body },
    ),

  listJobs: (params: { limit?: number } = {}): Promise<ReportJob[]> => {
    const qs = new URLSearchParams();
    if (params.limit) qs.set('limit', String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiFetch<ReportJob[]>(`/admin/reports/jobs${suffix}`);
  },

  getJob: (jobId: string): Promise<ReportJob> =>
    apiFetch<ReportJob>(`/admin/reports/jobs/${jobId}`),

  /**
   * Fetch the finished report as a blob.
   *
   * This used to be `downloadUrl()`, a bare path handed to an anchor's href.
   * Two things were wrong with that and both were invisible: the path skipped
   * the `/api` base that `apiFetch` applies, so the browser asked the SPA's own
   * origin and Caddy's catch-all answered with index.html — the new tab landed
   * on the dashboard, looking like a UI glitch rather than a missing report.
   * And even with the prefix it would have been a 401, because the admin token
   * is a Bearer header and a plain navigation cannot carry one.
   */
  download: (jobId: string): Promise<Blob> =>
    apiFetchBlob(`/admin/reports/jobs/${jobId}/download`),
};
